import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { AbstractControl, FormArray, FormBuilder, FormControl, ValidationErrors } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import moment, { HTML5_FMT } from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, exhaustMap, take, takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { TimeModel } from '../../models/primitive/time.model';
import { TimeUtilities } from '../../utilities/time-utilities';
import { TimeManualInputComponent } from './time-manual-input/time-manual-input.component';
import { DateModel } from '../../models/primitive/date.model';

@Component({
    selector: 'pace-core-time-input',
    templateUrl: './time-input.component.html',
    styleUrls: ['./time-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false,
})
export class TimeInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public model: TimeModel;

    @Input()
    public dateModel: DateModel;

    @Output()
    public modelChange = new EventEmitter<TimeModel>();

    @Input()
    public parentForm: FormArray | undefined;

    @Input()
    public ariaLabel = 'Time Input';

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<TimeModel>>();

    @Output()
    public focusEvent = new EventEmitter<void>();

    public timeInputControl: FormControl;

    public faClock = faClock;

    private originalTime: moment.Moment;
    private previousValue: moment.Moment;

    private destroy$ = new Subject<void>();
    public timeManualInput$ = new Subject<void>();
    public useCustomTimeInput = false;
    private timeFormatString = '';
    public isInputManuallyUpdated = false;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        private readonly modalService: BsModalService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.useCustomTimeInput = this.configService.getUseCustomTimeInput();
    }

    public ngOnInit(): void {
        this.timeFormatString = this.useCustomTimeInput
            ? this.model.options.displaySeconds
                ? this.clientManager.getTimeWithSecondsFormat()
                : this.clientManager.getTimeFormat()
            : HTML5_FMT.TIME_SECONDS;

        this.previousValue = this.model.value.clone();
        this.originalTime = this.model.value.clone();

        const timeValidator = (control: AbstractControl): ValidationErrors | null => this.checkValidity(control);
        this.timeInputControl = this.formBuilder.control(
            {
                value: this.model.value.isValid() ? this.model.value.format(this.timeFormatString) : undefined,
                disabled: this.model.options.readonly,
            },
            timeValidator,
        );

        this.timeInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    // if the value has changed externally
                    // then we don't need to emit model change event

                    if (this.timeInputControl.dirty || this.isInputManuallyUpdated) {
                        const oldDate = this.model.value.clone();
                        if (this.timeInputControl.errors) {
                            if (newVal === '' && this.originalTime.isValid()) {
                                this.timeInputControl.setValue(this.originalTime.format(this.timeFormatString));
                            }
                        }
                        this.model.value = TimeUtilities.setTimeHtml5(
                            this.model.value,
                            this.formatTime(<string>this.timeInputControl.value),
                        );
                        this.modelChange.emit(this.model);
                        this.valueChange.emit({ oldValue: oldDate, newValue: this.model.value });
                        this.isInputManuallyUpdated = false;
                    }
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.timeInputControl);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in TimeInputComponent -> ngOnInit -> parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }

        if (this.useCustomTimeInput) {
            this.timeManualInput$
                .asObservable()
                .pipe(
                    exhaustMap(() => {
                        const modalInstance = this.modalService.show(TimeManualInputComponent, {
                            backdrop: 'static',
                            class: 'modal-sm',
                            initialState: {
                                model: this.model,
                                dateModel: this.dateModel,
                            },
                            ariaLabelledBy: 'time-manual-input-modal-title',
                        });

                        return (
                            modalInstance.content?.modelChanged.pipe(
                                take(1),
                                tap((newTimeModel: TimeModel) => {
                                    this.isInputManuallyUpdated = true;
                                    this.timeInputControl.setValue(newTimeModel.value.format(this.timeFormatString));
                                }),
                                takeUntil(this.modalService.onHidden),
                            ) ?? of(undefined)
                        );
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe();
        }
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (this.timeInputControl) {
            // make sure that the control value always matches the model value, even if it was changed externally
            if (this.model.value.isValid() && !this.previousValue.isSame(this.model.value)) {
                this.previousValue = this.model.value.clone();
                this.timeInputControl.setValue(this.model.value.format(this.timeFormatString));
            }
            if (this.model.options.readonly && this.timeInputControl.enabled) {
                this.timeInputControl.disable({ onlySelf: true, emitEvent: false });
            }
            if (!this.model.options.readonly && this.timeInputControl.disabled) {
                this.timeInputControl.enable({ onlySelf: true, emitEvent: false });
            }
        }

        if (changesObj && changesObj.model && this.timeInputControl && this.model.value.isValid()) {
            this.model = <TimeModel>changesObj.model.currentValue;
            this.timeInputControl.setValue(this.model.value.format(this.timeFormatString));
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.timeInputControl);
            this.parentForm.removeAt(index);
        }
    }

    public onFocus(): void {
        this.focusEvent.emit();
    }

    public onClick(): void {
        if (!this.model.options.readonly) {
            this.timeManualInput$.next();
        }
    }

    public checkValidity(control: AbstractControl): ValidationErrors | null {
        const errors: ValidationErrors = {};
        if (!control.value) {
            errors.required = { required: 'Date is not selected' };
            return errors;
        }

        const valueClone = this.model.value.isValid() ? this.model.value.clone() : moment();

        const potentialValue = TimeUtilities.setTimeHtml5(valueClone, this.formatTime(<string>control.value));

        const potentialValueDate = potentialValue.clone().startOf('day');
        const minValueDate = this.model.options.minValue.clone().startOf('day');
        const maxValueDate = this.model.options.maxValue.clone().startOf('day');

        let formatPattern = this.clientManager.getTimeFormat();
        if (
            minValueDate.isBefore(maxValueDate) ||
            !(potentialValueDate.isSame(minValueDate) && potentialValueDate.isSame(maxValueDate))
        ) {
            formatPattern = this.configService.getDateTimeFormatPattern();
        }

        if (potentialValue.isBefore(this.model.options.minValue)) {
            errors.minValue = {
                text: `${this.translate.instant('TIME_INPUT_SELECT_TIME_RANGE_TEXT')} ${this.translate.instant(
                    'TIME_INPUT_VALID_TIME_RANGE_FROM_TEXT',
                    {
                        minTime: this.model.options.minValue.format(formatPattern),
                    },
                )}`,
            };
        }

        if (potentialValue.isAfter(this.model.options.maxValue)) {
            errors.maxValue = {
                text: `${this.translate.instant('TIME_INPUT_SELECT_TIME_RANGE_TEXT')} ${this.translate.instant(
                    'TIME_INPUT_VALID_TIME_RANGE_UPTO_TEXT',
                    {
                        maxTime: this.model.options.maxValue.format(formatPattern),
                    },
                )}`,
            };
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    private formatTime(timeInputValue: string): string {
        return this.useCustomTimeInput && timeInputValue && timeInputValue.includes('M')
            ? TimeUtilities.convertTimeFrom12hoursTo24hoursFormat(timeInputValue)
            : timeInputValue;
    }
}
